import type { ShotKey, BodySilhouette } from "./types"

// Reference illustrations the photo step shows the user above
// "Take Photo." Stroke-only inspection-template line art across
// five body silhouettes (sedan / suv / hatchback / pickup / van),
// chosen from the decoded VIN's `type`. Driver and passenger share
// one side drawing — passenger is the side view mirrored.
//
// Keeping the visual vocabulary identical across body types lets
// the user read them as one drawing system: same stroke weights,
// same paper grid, same camera framing.

const BODY = "#3f3f46"   // body lines + main outlines
const GLASS = "#3f3f46"  // window outlines (same hue, lighter weight)
const DETAIL = "#71717a" // door cuts, handles, grille bars
const LIGHT = "#a1a1aa"  // tertiary detail (rim spokes, vents)

interface ReferenceImageProps {
  shot: ShotKey
  bodyType?: BodySilhouette
}

export function ReferenceImage({ shot, bodyType = "sedan" }: ReferenceImageProps) {
  const isSide = shot === "driver" || shot === "passenger"
  const mirrored = shot === "passenger"

  const ViewSvg = pickView(bodyType, shot)

  return (
    <div
      className={
        "relative rounded-2xl overflow-hidden border border-zinc-200/80 shadow-sm bg-white " +
        (isSide ? "aspect-[12/5]" : "aspect-[7/5]")
      }
    >
      {/* Subtle paper grid */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.32]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(228, 228, 231, 0.55) 1px, transparent 1px), linear-gradient(90deg, rgba(228, 228, 231, 0.55) 1px, transparent 1px)",
          backgroundSize: "16px 16px",
        }}
      />

      <div className="absolute inset-0 p-3 flex items-center justify-center">
        <div
          className="w-full h-full"
          style={mirrored ? { transform: "scaleX(-1)" } : undefined}
        >
          <ViewSvg />
        </div>
      </div>

      {/* Tag — small label, top-left */}
      <div className="absolute left-3 top-3 px-2 py-0.5 rounded-md bg-white/85 border border-zinc-200/70 text-[9.5px] font-bold uppercase tracking-[0.16em] text-zinc-500">
        {labelFor(shot)}
      </div>
    </div>
  )
}

function labelFor(shot: ShotKey): string {
  switch (shot) {
    case "front": return "Front"
    case "driver": return "Driver"
    case "passenger": return "Passenger"
    case "rear": return "Rear"
  }
}

function pickView(
  body: BodySilhouette,
  shot: ShotKey,
): () => React.ReactElement {
  if (shot === "front") {
    return FRONT_VIEWS[body]
  }
  if (shot === "rear") {
    return REAR_VIEWS[body]
  }
  return SIDE_VIEWS[body]
}

const SIDE_VIEWS: Record<BodySilhouette, () => React.ReactElement> = {
  sedan:     SedanSide,
  suv:       SuvSide,
  hatchback: HatchbackSide,
  pickup:    PickupSide,
  van:       VanSide,
}

const FRONT_VIEWS: Record<BodySilhouette, () => React.ReactElement> = {
  sedan:     SedanFront,
  suv:       SuvFront,
  hatchback: SedanFront,    // hatchback front mostly identical to sedan
  pickup:    PickupFront,
  van:       VanFront,
}

const REAR_VIEWS: Record<BodySilhouette, () => React.ReactElement> = {
  sedan:     SedanRear,
  suv:       SuvRear,
  hatchback: HatchbackRear,
  pickup:    PickupRear,
  van:       VanRear,
}

/* ─────────────────────────────────────────────────────
   Side views — viewBox 480 × 200, ground at y≈180.
   Wheels are full circles (tire + rim + hub), drawn AFTER
   the body so they sit cleanly inside the wheel arches.
   ───────────────────────────────────────────────────── */

function Wheel({ cx, r = 22 }: { cx: number; r?: number }) {
  return (
    <g>
      <circle cx={cx} cy={160} r={r} fill="white" stroke={BODY} strokeWidth="1.4" />
      <circle cx={cx} cy={160} r={r * 0.5} fill="none" stroke={LIGHT} strokeWidth="0.8" />
      <circle cx={cx} cy={160} r={r * 0.12} fill={DETAIL} />
      {/* Five spokes, hint only */}
      {[0, 72, 144, 216, 288].map((deg, i) => {
        const rad = (deg * Math.PI) / 180
        const x1 = cx + Math.cos(rad) * (r * 0.18)
        const y1 = 160 + Math.sin(rad) * (r * 0.18)
        const x2 = cx + Math.cos(rad) * (r * 0.46)
        const y2 = 160 + Math.sin(rad) * (r * 0.46)
        return (
          <line
            key={i}
            x1={x1} y1={y1} x2={x2} y2={y2}
            stroke={LIGHT}
            strokeWidth="0.6"
          />
        )
      })}
    </g>
  )
}

function GroundShadow() {
  return (
    <ellipse cx="240" cy="190" rx="200" ry="3" fill={LIGHT} opacity="0.25" />
  )
}

function SideFrame({ children }: { children: React.ReactNode }) {
  return (
    <svg
      viewBox="0 0 480 200"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-full"
      preserveAspectRatio="xMidYMid meet"
    >
      <GroundShadow />
      {children}
    </svg>
  )
}

function SedanSide() {
  return (
    <SideFrame>
      {/* Body — closed path including wheel arches as concave dips */}
      <path
        fill="none"
        stroke={BODY}
        strokeWidth="1.5"
        strokeLinejoin="round"
        d="
          M 22 148
          L 22 134 Q 22 124 32 122
          L 60 116 Q 100 104 168 92
          L 240 44 L 296 44 L 360 78
          Q 376 84 388 90 L 432 96
          Q 458 100 458 116 L 458 134 L 458 148
          L 408 148
          A 24 24 0 0 0 360 148
          L 134 148
          A 24 24 0 0 0 86 148
          Z"
      />
      {/* Greenhouse (windshield → roof → rear glass) */}
      <path
        fill="none"
        stroke={GLASS}
        strokeWidth="1.0"
        d="M 174 88 L 240 50 L 296 50 L 358 80"
      />
      {/* B-pillar inside greenhouse */}
      <line x1="270" y1="50" x2="270" y2="84" stroke={GLASS} strokeWidth="0.8" />
      {/* Door cuts */}
      <line x1="172" y1="92" x2="172" y2="148" stroke={DETAIL} strokeWidth="0.7" />
      <line x1="270" y1="84" x2="270" y2="148" stroke={DETAIL} strokeWidth="0.7" />
      <line x1="336" y1="76" x2="336" y2="148" stroke={DETAIL} strokeWidth="0.7" />
      {/* Door handles */}
      <line x1="200" y1="124" x2="232" y2="124" stroke={DETAIL} strokeWidth="0.9" strokeLinecap="round" />
      <line x1="288" y1="124" x2="320" y2="124" stroke={DETAIL} strokeWidth="0.9" strokeLinecap="round" />
      {/* Side mirror */}
      <path
        fill="none"
        stroke={BODY}
        strokeWidth="0.9"
        d="M 174 86 L 162 80 Q 158 76 165 75 L 178 84"
      />
      {/* Headlight */}
      <path
        fill="none"
        stroke={BODY}
        strokeWidth="0.9"
        d="M 28 126 L 56 122 L 56 132 L 28 132 Z"
      />
      {/* Taillight */}
      <path
        fill="none"
        stroke={BODY}
        strokeWidth="0.9"
        d="M 426 126 L 454 122 L 454 132 L 426 132 Z"
      />
      {/* Rocker panel — subtle line below doors */}
      <line x1="138" y1="148" x2="358" y2="148" stroke={DETAIL} strokeWidth="0.5" opacity="0.7" />

      <Wheel cx={110} />
      <Wheel cx={384} />
    </SideFrame>
  )
}

function SuvSide() {
  return (
    <SideFrame>
      <path
        fill="none"
        stroke={BODY}
        strokeWidth="1.5"
        strokeLinejoin="round"
        d="
          M 22 148
          L 22 130 Q 22 118 32 116
          L 56 110 Q 88 100 124 92
          L 184 44 L 386 44
          Q 416 46 432 60 L 446 110
          Q 458 118 458 130 L 458 148
          L 414 148
          A 26 26 0 0 0 362 148
          L 134 148
          A 26 26 0 0 0 82 148
          Z"
      />
      {/* Greenhouse */}
      <path
        fill="none"
        stroke={GLASS}
        strokeWidth="1.0"
        d="M 130 90 L 184 50 L 386 50 L 432 84"
      />
      {/* Pillars (A, B, C, D) */}
      <line x1="184" y1="50" x2="184" y2="92" stroke={GLASS} strokeWidth="0.8" />
      <line x1="252" y1="50" x2="252" y2="148" stroke={DETAIL} strokeWidth="0.7" />
      <line x1="320" y1="50" x2="320" y2="148" stroke={DETAIL} strokeWidth="0.7" />
      <line x1="386" y1="50" x2="386" y2="92" stroke={GLASS} strokeWidth="0.8" />
      {/* Door cuts */}
      <line x1="130" y1="92" x2="130" y2="148" stroke={DETAIL} strokeWidth="0.7" />
      <line x1="386" y1="92" x2="386" y2="148" stroke={DETAIL} strokeWidth="0.7" />
      {/* Door handles */}
      <line x1="200" y1="124" x2="232" y2="124" stroke={DETAIL} strokeWidth="0.9" strokeLinecap="round" />
      <line x1="340" y1="124" x2="372" y2="124" stroke={DETAIL} strokeWidth="0.9" strokeLinecap="round" />
      {/* Roof rails */}
      <line x1="200" y1="44" x2="370" y2="44" stroke={DETAIL} strokeWidth="0.5" opacity="0.7" />
      {/* Mirror */}
      <path fill="none" stroke={BODY} strokeWidth="0.9" d="M 130 86 L 118 78 Q 114 74 122 73 L 134 82" />
      {/* Lights */}
      <path fill="none" stroke={BODY} strokeWidth="0.9" d="M 26 116 L 56 112 L 56 124 L 26 124 Z" />
      <path fill="none" stroke={BODY} strokeWidth="0.9" d="M 426 116 L 456 112 L 456 124 L 426 124 Z" />
      {/* Roof rack rail */}
      <line x1="195" y1="40" x2="375" y2="40" stroke={DETAIL} strokeWidth="0.5" />

      <Wheel cx={108} r={26} />
      <Wheel cx={388} r={26} />
    </SideFrame>
  )
}

function HatchbackSide() {
  return (
    <SideFrame>
      <path
        fill="none"
        stroke={BODY}
        strokeWidth="1.5"
        strokeLinejoin="round"
        d="
          M 28 148
          L 28 132 Q 28 122 38 120
          L 62 114 Q 96 102 154 92
          L 216 46 L 308 46
          Q 348 50 380 80 L 432 130
          Q 446 140 446 148
          L 408 148
          A 24 24 0 0 0 360 148
          L 138 148
          A 24 24 0 0 0 90 148
          Z"
      />
      {/* Greenhouse */}
      <path
        fill="none"
        stroke={GLASS}
        strokeWidth="1.0"
        d="M 160 90 L 216 52 L 308 52 L 376 84"
      />
      {/* B-pillar */}
      <line x1="252" y1="52" x2="252" y2="84" stroke={GLASS} strokeWidth="0.8" />
      {/* Door cuts */}
      <line x1="158" y1="92" x2="158" y2="148" stroke={DETAIL} strokeWidth="0.7" />
      <line x1="252" y1="84" x2="252" y2="148" stroke={DETAIL} strokeWidth="0.7" />
      {/* Hatch cut — diagonal following the rear hatch line */}
      <line x1="318" y1="58" x2="408" y2="138" stroke={DETAIL} strokeWidth="0.7" />
      {/* Door handles */}
      <line x1="186" y1="124" x2="218" y2="124" stroke={DETAIL} strokeWidth="0.9" strokeLinecap="round" />
      <line x1="278" y1="124" x2="310" y2="124" stroke={DETAIL} strokeWidth="0.9" strokeLinecap="round" />
      {/* Mirror */}
      <path fill="none" stroke={BODY} strokeWidth="0.9" d="M 160 86 L 148 78 Q 144 74 152 73 L 164 82" />
      {/* Lights */}
      <path fill="none" stroke={BODY} strokeWidth="0.9" d="M 32 124 L 60 120 L 60 130 L 32 130 Z" />
      <path fill="none" stroke={BODY} strokeWidth="0.9" d="M 408 134 L 432 134 L 432 142 L 412 142 Z" />

      <Wheel cx={114} />
      <Wheel cx={384} />
    </SideFrame>
  )
}

function PickupSide() {
  return (
    <SideFrame>
      {/* Hood + cab + bed in one outline. The bed top dips below the
          cab roof so the silhouette reads as a truck, not an SUV. */}
      <path
        fill="none"
        stroke={BODY}
        strokeWidth="1.5"
        strokeLinejoin="round"
        d="
          M 22 148
          L 22 132 Q 22 122 34 120
          L 60 114 Q 90 104 132 96
          L 184 48 L 248 48
          L 274 86
          L 444 86
          L 458 86 L 458 148
          L 408 148
          A 24 24 0 0 0 360 148
          L 134 148
          A 24 24 0 0 0 86 148
          Z"
      />
      {/* Cab greenhouse */}
      <path
        fill="none"
        stroke={GLASS}
        strokeWidth="1.0"
        d="M 138 92 L 184 54 L 248 54 L 270 80"
      />
      {/* B-pillar (cab is a single door each side typically) */}
      <line x1="218" y1="54" x2="218" y2="148" stroke={DETAIL} strokeWidth="0.7" />
      {/* Door cut at front of cab */}
      <line x1="138" y1="94" x2="138" y2="148" stroke={DETAIL} strokeWidth="0.7" />
      {/* Bed wall top vs cab back */}
      <line x1="270" y1="86" x2="270" y2="120" stroke={DETAIL} strokeWidth="0.7" />
      {/* Tailgate seam */}
      <line x1="444" y1="86" x2="444" y2="148" stroke={DETAIL} strokeWidth="0.7" />
      {/* Bed rail line */}
      <line x1="280" y1="92" x2="440" y2="92" stroke={DETAIL} strokeWidth="0.5" opacity="0.7" />
      {/* Door handle */}
      <line x1="170" y1="124" x2="206" y2="124" stroke={DETAIL} strokeWidth="0.9" strokeLinecap="round" />
      {/* Mirror — large truck mirror */}
      <path fill="none" stroke={BODY} strokeWidth="0.9" d="M 138 84 L 122 76 Q 118 70 128 70 L 144 80" />
      {/* Lights */}
      <path fill="none" stroke={BODY} strokeWidth="0.9" d="M 28 122 L 56 118 L 56 130 L 28 130 Z" />
      <path fill="none" stroke={BODY} strokeWidth="0.9" d="M 428 96 L 452 96 L 452 106 L 428 106 Z" />

      <Wheel cx={108} r={24} />
      <Wheel cx={384} r={24} />
    </SideFrame>
  )
}

function VanSide() {
  return (
    <SideFrame>
      <path
        fill="none"
        stroke={BODY}
        strokeWidth="1.5"
        strokeLinejoin="round"
        d="
          M 22 148
          L 22 124 Q 22 114 32 112
          L 56 106 L 86 96
          L 116 40
          L 416 40
          Q 442 42 446 56 L 458 110
          L 458 148
          L 414 148
          A 24 24 0 0 0 366 148
          L 138 148
          A 24 24 0 0 0 90 148
          Z"
      />
      {/* Greenhouse — windshield + side windows + rear */}
      <path
        fill="none"
        stroke={GLASS}
        strokeWidth="1.0"
        d="M 96 92 L 116 46 L 416 46 L 446 84"
      />
      {/* Pillars */}
      <line x1="116" y1="46" x2="116" y2="92" stroke={GLASS} strokeWidth="0.8" />
      <line x1="200" y1="46" x2="200" y2="148" stroke={DETAIL} strokeWidth="0.7" />
      <line x1="280" y1="46" x2="280" y2="148" stroke={DETAIL} strokeWidth="0.7" />
      <line x1="416" y1="46" x2="416" y2="92" stroke={GLASS} strokeWidth="0.8" />
      {/* Door cut at slider */}
      <line x1="200" y1="92" x2="280" y2="92" stroke={DETAIL} strokeWidth="0.5" opacity="0.5" />
      {/* Driver door cut */}
      <line x1="160" y1="94" x2="160" y2="148" stroke={DETAIL} strokeWidth="0.7" />
      {/* Sliding-door handle */}
      <line x1="220" y1="124" x2="260" y2="124" stroke={DETAIL} strokeWidth="0.9" strokeLinecap="round" />
      {/* Driver door handle */}
      <line x1="180" y1="118" x2="200" y2="118" stroke={DETAIL} strokeWidth="0.9" strokeLinecap="round" />
      {/* Mirror */}
      <path fill="none" stroke={BODY} strokeWidth="0.9" d="M 96 84 L 80 70 Q 74 64 86 64 L 104 80" />
      {/* Lights */}
      <path fill="none" stroke={BODY} strokeWidth="0.9" d="M 28 116 L 54 112 L 54 124 L 28 124 Z" />
      <path fill="none" stroke={BODY} strokeWidth="0.9" d="M 426 110 L 454 108 L 454 122 L 426 122 Z" />

      <Wheel cx={108} />
      <Wheel cx={388} />
    </SideFrame>
  )
}

/* ─────────────────────────────────────────────────────
   Front views — viewBox 280 × 200.
   Composition: bumper + grille + headlights + windshield
   + roof + side mirrors. License plate centered.
   ───────────────────────────────────────────────────── */

function FrontFrame({ children }: { children: React.ReactNode }) {
  return (
    <svg
      viewBox="0 0 280 200"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-full"
      preserveAspectRatio="xMidYMid meet"
    >
      <ellipse cx="140" cy="190" rx="116" ry="3" fill={LIGHT} opacity="0.25" />
      {children}
    </svg>
  )
}

function SedanFront() {
  return (
    <FrontFrame>
      {/* Body silhouette */}
      <path
        fill="none"
        stroke={BODY}
        strokeWidth="1.5"
        strokeLinejoin="round"
        d="
          M 36 174
          L 36 156 Q 34 138 42 130
          L 56 116 Q 96 110 140 110 Q 184 110 224 116
          L 238 130 Q 246 138 244 156
          L 244 174 Z"
      />
      {/* Hood seam */}
      <path
        fill="none"
        stroke={DETAIL}
        strokeWidth="0.7"
        d="M 60 122 Q 140 112 220 122"
      />
      {/* Windshield (above body) */}
      <path
        fill="none"
        stroke={GLASS}
        strokeWidth="1.0"
        d="M 56 116 L 86 60 L 194 60 L 224 116"
      />
      {/* Roof */}
      <line x1="86" y1="60" x2="194" y2="60" stroke={BODY} strokeWidth="1.4" />
      {/* Headlights — angled lozenges */}
      <path fill="none" stroke={BODY} strokeWidth="1.0"
        d="M 42 132 Q 60 130 82 134 L 82 144 Q 60 148 42 144 Z" />
      <path fill="none" stroke={BODY} strokeWidth="1.0"
        d="M 198 134 Q 220 130 238 132 L 238 144 Q 220 148 198 144 Z" />
      {/* Grille */}
      <rect x="92" y="138" width="96" height="22" fill="none" stroke={BODY} strokeWidth="1.0" rx="2" />
      {/* Grille bars */}
      <line x1="98" y1="144" x2="182" y2="144" stroke={DETAIL} strokeWidth="0.5" />
      <line x1="98" y1="149" x2="182" y2="149" stroke={DETAIL} strokeWidth="0.5" />
      <line x1="98" y1="154" x2="182" y2="154" stroke={DETAIL} strokeWidth="0.5" />
      {/* License plate */}
      <rect x="116" y="166" width="48" height="12" fill="none" stroke={DETAIL} strokeWidth="0.7" rx="1" />
      {/* Bumper bottom edge */}
      <line x1="56" y1="174" x2="224" y2="174" stroke={DETAIL} strokeWidth="0.5" />
      {/* Side mirrors */}
      <path fill="none" stroke={BODY} strokeWidth="1.0"
        d="M 30 86 Q 22 84 22 90 L 22 96 Q 24 100 32 100 L 38 96" />
      <path fill="none" stroke={BODY} strokeWidth="1.0"
        d="M 250 86 Q 258 84 258 90 L 258 96 Q 256 100 248 100 L 242 96" />
      {/* Manufacturer badge dot */}
      <circle cx="140" cy="148" r="3" fill="none" stroke={DETAIL} strokeWidth="0.6" />
    </FrontFrame>
  )
}

function SuvFront() {
  return (
    <FrontFrame>
      <path
        fill="none"
        stroke={BODY}
        strokeWidth="1.5"
        strokeLinejoin="round"
        d="
          M 30 174
          L 30 138 Q 28 122 36 114
          L 50 100 Q 92 92 140 92 Q 188 92 230 100
          L 244 114 Q 252 122 250 138
          L 250 174 Z"
      />
      <path fill="none" stroke={DETAIL} strokeWidth="0.7"
        d="M 54 110 Q 140 100 226 110" />
      <path fill="none" stroke={GLASS} strokeWidth="1.0"
        d="M 50 100 L 76 50 L 204 50 L 230 100" />
      <line x1="76" y1="50" x2="204" y2="50" stroke={BODY} strokeWidth="1.4" />
      {/* Roof rails */}
      <line x1="80" y1="46" x2="200" y2="46" stroke={DETAIL} strokeWidth="0.5" />
      {/* Headlights */}
      <path fill="none" stroke={BODY} strokeWidth="1.0"
        d="M 38 122 L 80 122 L 80 134 L 38 134 Z" />
      <path fill="none" stroke={BODY} strokeWidth="1.0"
        d="M 200 122 L 242 122 L 242 134 L 200 134 Z" />
      {/* Grille — bigger, two-bar */}
      <rect x="86" y="124" width="108" height="32" fill="none" stroke={BODY} strokeWidth="1.0" rx="2" />
      <line x1="92" y1="134" x2="188" y2="134" stroke={DETAIL} strokeWidth="0.5" />
      <line x1="92" y1="142" x2="188" y2="142" stroke={DETAIL} strokeWidth="0.5" />
      <line x1="92" y1="150" x2="188" y2="150" stroke={DETAIL} strokeWidth="0.5" />
      {/* Skid plate hint */}
      <path fill="none" stroke={DETAIL} strokeWidth="0.7"
        d="M 70 168 L 100 162 L 180 162 L 210 168" />
      {/* License plate */}
      <rect x="116" y="164" width="48" height="12" fill="none" stroke={DETAIL} strokeWidth="0.7" rx="1" />
      {/* Mirrors */}
      <path fill="none" stroke={BODY} strokeWidth="1.0"
        d="M 24 76 Q 14 74 14 80 L 14 88 Q 16 92 26 92 L 32 88" />
      <path fill="none" stroke={BODY} strokeWidth="1.0"
        d="M 256 76 Q 266 74 266 80 L 266 88 Q 264 92 254 92 L 248 88" />
      <circle cx="140" cy="140" r="3.5" fill="none" stroke={DETAIL} strokeWidth="0.6" />
    </FrontFrame>
  )
}

function PickupFront() {
  return (
    <FrontFrame>
      <path
        fill="none"
        stroke={BODY}
        strokeWidth="1.5"
        strokeLinejoin="round"
        d="
          M 22 174
          L 22 138 Q 20 124 28 116
          L 44 100 Q 90 92 140 92 Q 190 92 236 100
          L 252 116 Q 260 124 258 138
          L 258 174 Z"
      />
      <path fill="none" stroke={DETAIL} strokeWidth="0.7"
        d="M 48 112 Q 140 100 232 112" />
      <path fill="none" stroke={GLASS} strokeWidth="1.0"
        d="M 44 100 L 70 52 L 210 52 L 236 100" />
      <line x1="70" y1="52" x2="210" y2="52" stroke={BODY} strokeWidth="1.4" />
      {/* Big square headlights */}
      <rect x="32" y="118" width="48" height="20" fill="none" stroke={BODY} strokeWidth="1.0" rx="1" />
      <rect x="200" y="118" width="48" height="20" fill="none" stroke={BODY} strokeWidth="1.0" rx="1" />
      {/* Big slatted grille */}
      <rect x="86" y="118" width="108" height="40" fill="none" stroke={BODY} strokeWidth="1.0" rx="2" />
      {[126, 134, 142, 150].map((y) => (
        <line key={y} x1="92" y1={y} x2="188" y2={y} stroke={DETAIL} strokeWidth="0.5" />
      ))}
      {/* Bumper face */}
      <line x1="36" y1="160" x2="244" y2="160" stroke={DETAIL} strokeWidth="0.7" />
      <rect x="116" y="166" width="48" height="12" fill="none" stroke={DETAIL} strokeWidth="0.7" rx="1" />
      {/* Tow hooks (small) */}
      <circle cx="44" cy="166" r="2" fill="none" stroke={DETAIL} strokeWidth="0.6" />
      <circle cx="236" cy="166" r="2" fill="none" stroke={DETAIL} strokeWidth="0.6" />
      {/* Mirrors — large boxy truck mirrors */}
      <rect x="10" y="74" width="14" height="22" fill="none" stroke={BODY} strokeWidth="1.0" rx="1" />
      <rect x="256" y="74" width="14" height="22" fill="none" stroke={BODY} strokeWidth="1.0" rx="1" />
    </FrontFrame>
  )
}

function VanFront() {
  return (
    <FrontFrame>
      <path
        fill="none"
        stroke={BODY}
        strokeWidth="1.5"
        strokeLinejoin="round"
        d="
          M 28 174
          L 28 132 Q 26 116 34 108
          L 48 90 Q 90 80 140 80 Q 190 80 232 90
          L 246 108 Q 254 116 252 132
          L 252 174 Z"
      />
      <path fill="none" stroke={DETAIL} strokeWidth="0.7"
        d="M 52 102 Q 140 92 228 102" />
      {/* Big tall windshield */}
      <path fill="none" stroke={GLASS} strokeWidth="1.0"
        d="M 48 90 L 60 36 L 220 36 L 232 90" />
      <line x1="60" y1="36" x2="220" y2="36" stroke={BODY} strokeWidth="1.4" />
      {/* Headlights */}
      <path fill="none" stroke={BODY} strokeWidth="1.0"
        d="M 36 116 L 84 112 L 84 128 L 36 128 Z" />
      <path fill="none" stroke={BODY} strokeWidth="1.0"
        d="M 196 112 L 244 116 L 244 128 L 196 128 Z" />
      {/* Wide grille */}
      <rect x="90" y="116" width="100" height="20" fill="none" stroke={BODY} strokeWidth="1.0" rx="1" />
      <line x1="96" y1="122" x2="184" y2="122" stroke={DETAIL} strokeWidth="0.5" />
      <line x1="96" y1="130" x2="184" y2="130" stroke={DETAIL} strokeWidth="0.5" />
      {/* Bumper */}
      <rect x="40" y="142" width="200" height="22" fill="none" stroke={DETAIL} strokeWidth="0.7" rx="2" />
      <rect x="116" y="148" width="48" height="12" fill="none" stroke={DETAIL} strokeWidth="0.7" rx="1" />
      {/* Mirrors — large van mirrors */}
      <path fill="none" stroke={BODY} strokeWidth="1.0"
        d="M 22 60 Q 12 58 12 66 L 12 80 Q 14 84 24 84 L 30 80" />
      <path fill="none" stroke={BODY} strokeWidth="1.0"
        d="M 258 60 Q 268 58 268 66 L 268 80 Q 266 84 256 84 L 250 80" />
    </FrontFrame>
  )
}

/* ─────────────────────────────────────────────────────
   Rear views — viewBox 280 × 200.
   Composition: bumper + license plate + tail-lights +
   trunk/hatch seam + rear glass + roof.
   ───────────────────────────────────────────────────── */

function SedanRear() {
  return (
    <FrontFrame>
      <path
        fill="none"
        stroke={BODY}
        strokeWidth="1.5"
        strokeLinejoin="round"
        d="
          M 36 174
          L 36 156 Q 34 138 42 130
          L 56 116 Q 96 108 140 108 Q 184 108 224 116
          L 238 130 Q 246 138 244 156
          L 244 174 Z"
      />
      {/* Trunk lid seam — horizontal across mid-back */}
      <path
        fill="none"
        stroke={DETAIL}
        strokeWidth="0.7"
        d="M 56 124 Q 140 116 224 124"
      />
      {/* Rear glass */}
      <path fill="none" stroke={GLASS} strokeWidth="1.0"
        d="M 56 116 L 86 64 L 194 64 L 224 116" />
      <line x1="86" y1="64" x2="194" y2="64" stroke={BODY} strokeWidth="1.4" />
      {/* Tail-light pair — angled lozenges similar to headlights */}
      <path fill="none" stroke={BODY} strokeWidth="1.0"
        d="M 42 130 Q 60 128 86 132 L 86 142 Q 60 146 42 142 Z" />
      <path fill="none" stroke={BODY} strokeWidth="1.0"
        d="M 194 132 Q 220 128 238 130 L 238 142 Q 220 146 194 142 Z" />
      {/* Brake-light strip across the trunk lid */}
      <line x1="100" y1="120" x2="180" y2="120" stroke={DETAIL} strokeWidth="0.6" />
      {/* License plate — centered on bumper */}
      <rect x="108" y="148" width="64" height="18" fill="none" stroke={DETAIL} strokeWidth="0.8" rx="1" />
      {/* Brand badge */}
      <circle cx="140" cy="138" r="3" fill="none" stroke={DETAIL} strokeWidth="0.6" />
      {/* Bumper bottom edge */}
      <line x1="56" y1="174" x2="224" y2="174" stroke={DETAIL} strokeWidth="0.5" />
      {/* Exhaust tip hint */}
      <circle cx="200" cy="172" r="3" fill="none" stroke={DETAIL} strokeWidth="0.6" />
    </FrontFrame>
  )
}

function SuvRear() {
  return (
    <FrontFrame>
      <path
        fill="none"
        stroke={BODY}
        strokeWidth="1.5"
        strokeLinejoin="round"
        d="
          M 30 174
          L 30 138 Q 28 122 36 114
          L 50 100 Q 92 92 140 92 Q 188 92 230 100
          L 244 114 Q 252 122 250 138
          L 250 174 Z"
      />
      {/* Hatch/glass full-width */}
      <path fill="none" stroke={GLASS} strokeWidth="1.0"
        d="M 50 100 L 76 50 L 204 50 L 230 100" />
      <line x1="76" y1="50" x2="204" y2="50" stroke={BODY} strokeWidth="1.4" />
      {/* Wiper hint */}
      <line x1="120" y1="92" x2="100" y2="74" stroke={DETAIL} strokeWidth="0.6" />
      {/* Tail-lights — vertical strips on the corners */}
      <rect x="38" y="106" width="18" height="48" fill="none" stroke={BODY} strokeWidth="1.0" rx="1.5" />
      <rect x="224" y="106" width="18" height="48" fill="none" stroke={BODY} strokeWidth="1.0" rx="1.5" />
      {/* Hatch seam */}
      <path fill="none" stroke={DETAIL} strokeWidth="0.7"
        d="M 60 110 Q 140 102 220 110" />
      {/* Liftgate handle / brand badge */}
      <line x1="120" y1="124" x2="160" y2="124" stroke={DETAIL} strokeWidth="0.7" strokeLinecap="round" />
      <circle cx="140" cy="138" r="3.5" fill="none" stroke={DETAIL} strokeWidth="0.6" />
      <rect x="108" y="148" width="64" height="18" fill="none" stroke={DETAIL} strokeWidth="0.8" rx="1" />
      <line x1="50" y1="174" x2="230" y2="174" stroke={DETAIL} strokeWidth="0.5" />
    </FrontFrame>
  )
}

function HatchbackRear() {
  return (
    <FrontFrame>
      <path
        fill="none"
        stroke={BODY}
        strokeWidth="1.5"
        strokeLinejoin="round"
        d="
          M 32 174
          L 32 144 Q 30 128 38 120
          L 52 104 Q 94 96 140 96 Q 186 96 228 104
          L 242 120 Q 250 128 248 144
          L 248 174 Z"
      />
      <path fill="none" stroke={GLASS} strokeWidth="1.0"
        d="M 52 104 L 78 52 L 202 52 L 228 104" />
      <line x1="78" y1="52" x2="202" y2="52" stroke={BODY} strokeWidth="1.4" />
      {/* Wiper */}
      <line x1="118" y1="96" x2="98" y2="78" stroke={DETAIL} strokeWidth="0.6" />
      {/* Tail lights — wraparound corner bars */}
      <path fill="none" stroke={BODY} strokeWidth="1.0"
        d="M 38 110 L 80 110 L 80 122 L 38 122 Z" />
      <path fill="none" stroke={BODY} strokeWidth="1.0"
        d="M 200 110 L 242 110 L 242 122 L 200 122 Z" />
      {/* Liftgate seam */}
      <path fill="none" stroke={DETAIL} strokeWidth="0.7"
        d="M 60 130 Q 140 122 220 130" />
      <circle cx="140" cy="138" r="3" fill="none" stroke={DETAIL} strokeWidth="0.6" />
      <rect x="116" y="150" width="48" height="14" fill="none" stroke={DETAIL} strokeWidth="0.7" rx="1" />
    </FrontFrame>
  )
}

function PickupRear() {
  return (
    <FrontFrame>
      {/* Tailgate up + bedwalls visible from behind */}
      <path
        fill="none"
        stroke={BODY}
        strokeWidth="1.5"
        strokeLinejoin="round"
        d="
          M 22 174
          L 22 76 Q 20 60 30 56
          L 36 56 L 244 56 L 250 56
          Q 260 60 258 76 L 258 174 Z"
      />
      {/* Cab roof line above tailgate */}
      <line x1="60" y1="42" x2="220" y2="42" stroke={BODY} strokeWidth="1.4" />
      <path fill="none" stroke={GLASS} strokeWidth="1.0"
        d="M 36 56 L 60 42 L 220 42 L 244 56" />
      {/* Tailgate seam */}
      <line x1="22" y1="76" x2="258" y2="76" stroke={DETAIL} strokeWidth="0.7" />
      {/* Tail lights */}
      <rect x="32" y="86" width="56" height="22" fill="none" stroke={BODY} strokeWidth="1.0" rx="1" />
      <rect x="192" y="86" width="56" height="22" fill="none" stroke={BODY} strokeWidth="1.0" rx="1" />
      {/* Brand badge / model badge */}
      <line x1="116" y1="100" x2="164" y2="100" stroke={DETAIL} strokeWidth="0.7" />
      {/* License plate */}
      <rect x="108" y="120" width="64" height="18" fill="none" stroke={DETAIL} strokeWidth="0.8" rx="1" />
      {/* Bumper */}
      <rect x="22" y="148" width="236" height="20" fill="none" stroke={DETAIL} strokeWidth="0.7" rx="2" />
      {/* Exhaust */}
      <circle cx="216" cy="170" r="4" fill="none" stroke={DETAIL} strokeWidth="0.6" />
    </FrontFrame>
  )
}

function VanRear() {
  return (
    <FrontFrame>
      <path
        fill="none"
        stroke={BODY}
        strokeWidth="1.5"
        strokeLinejoin="round"
        d="
          M 28 174
          L 28 60 Q 26 38 36 32
          L 244 32 Q 254 38 252 60 L 252 174 Z"
      />
      {/* Top window strip */}
      <rect x="42" y="44" width="196" height="36" fill="none" stroke={GLASS} strokeWidth="1.0" rx="1" />
      {/* Vertical center seam — split rear doors */}
      <line x1="140" y1="44" x2="140" y2="156" stroke={DETAIL} strokeWidth="0.7" strokeDasharray="3 2" />
      {/* Tail-light corner blocks */}
      <rect x="38" y="86" width="22" height="56" fill="none" stroke={BODY} strokeWidth="1.0" rx="1" />
      <rect x="220" y="86" width="22" height="56" fill="none" stroke={BODY} strokeWidth="1.0" rx="1" />
      {/* Door handles */}
      <line x1="100" y1="116" x2="124" y2="116" stroke={DETAIL} strokeWidth="0.9" strokeLinecap="round" />
      <line x1="156" y1="116" x2="180" y2="116" stroke={DETAIL} strokeWidth="0.9" strokeLinecap="round" />
      {/* License plate */}
      <rect x="108" y="148" width="64" height="14" fill="none" stroke={DETAIL} strokeWidth="0.7" rx="1" />
      {/* Bumper */}
      <line x1="34" y1="166" x2="246" y2="166" stroke={DETAIL} strokeWidth="0.5" />
    </FrontFrame>
  )
}
